import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { getHdPlanetDisplayConfigByPlanetId } from '../planet-grid/hd-planet-display-config';

/** Zijdekleuren van de planeetgrid: design rood, personality zwart. */
const HD_GATE_ACTIVATION_SIDE_COLORS: Readonly<Record<string, string>> = {
  design: '#c0392b',
  personality: '#1a1a1a',
};

/**
 * Compacte poortactivatie: planeetglyph + poort.lijn in de zijdekleur (design
 * rood, personality zwart), zoals in de planeetgrid. Gebruikt in de matchlijsten
 * van de poortzoektochten (poort-info, Locks & Keys); de tooltip noemt planeet,
 * zijde en poort.lijn voluit.
 */
@Component({
  selector: 'hd-gate-activation-chip',
  standalone: true,
  template: `
    <span class="hd-gate-activation-chip" [style.color]="color()" [attr.title]="tooltip()" [attr.data-side]="side()">
      <span class="hd-gate-activation-chip__glyph" aria-hidden="true">{{ glyph() }}</span>
      <span class="hd-gate-activation-chip__gate-line">{{ gate() }}.{{ line() }}</span>
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .hd-gate-activation-chip {
      display: inline-flex;
      align-items: baseline;
      gap: 1px;
      white-space: nowrap;
      font-weight: 700;
      font-size: 12px;
      line-height: 1;
    }

    .hd-gate-activation-chip__glyph {
      font-family: 'Astronomicon', sans-serif;
      font-size: 14px;
      font-weight: bold;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdGateActivationChipComponent {
  readonly gate = input.required<number>();
  readonly line = input.required<number>();
  /** "personality" of "design" (backend-vocabulaire, zelfde als de chart-activaties). */
  readonly side = input.required<string>();
  readonly planetId = input.required<number>();

  private readonly transloco = inject(TranslocoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly displayConfig = computed(() => getHdPlanetDisplayConfigByPlanetId(this.planetId()));

  protected readonly glyph = computed(() => this.displayConfig()?.astronomiconChar ?? '');

  protected readonly color = computed(() => HD_GATE_ACTIVATION_SIDE_COLORS[this.side()] ?? 'inherit');

  protected readonly tooltip = computed(() => {
    const lang = this.contentLang.effectiveLang();
    const config = this.displayConfig();
    const planetName = config
      ? this.planetInfo.planetTranslationsAllIn(lang)[config.key]?.name ?? config.label
      : '';
    const sideKey = this.side() === 'design' ? 'tabDesign' : 'tabPersonality';
    const sideLabel = this.transloco.translate<string>(`humanDesign.properties.${sideKey}`, undefined, lang);
    return [planetName, sideLabel, `${this.gate()}.${this.line()}`].filter(Boolean).join(' · ');
  });
}
